import React, { useState, useCallback } from "react";
import styled from "styled-components";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";

const DateRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const PresetButton = styled.button<{ isActive: boolean }>`
  padding: 8px 16px;
  border: 1px solid ${(props) => (props.isActive ? "#FF734F" : "#ebebeb")};
  border-radius: 8px;
  background: ${(props) => (props.isActive ? "#FF734F" : "#fff")};
  color: ${(props) => (props.isActive ? "#fff" : "#232129")};
  font-family: "Sharp Sans";
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.isActive ? "#FF734F" : "#f5f5f5")};
  }
`;

const CustomDateContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DateInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ebebeb;
  border-radius: 8px;
  font-family: "Sharp Sans";
  font-size: 14px;
  color: #232129;

  &:focus {
    outline: none;
    border-color: #ff734f;
  }
`;

const DateSeparator = styled.span`
  color: #6e6e6e;
  font-family: "Sharp Sans";
  font-size: 14px;
  font-weight: 500;
`;

interface DateRangePickerProps {
  onDateRangeChange: (startDate: string, endDate: string) => void;
  defaultRange?: "7days" | "30days" | "custom";
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  onDateRangeChange,
  defaultRange = "7days",
}) => {
  const [selectedRange, setSelectedRange] = useState<
    "7days" | "30days" | "custom"
  >(defaultRange);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const formatDate = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  const getLast7Days = useCallback(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 7);
    return {
      start: formatDate(startDate),
      end: formatDate(endDate),
    };
  }, []);

  const getLast30Days = useCallback(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 30);
    return {
      start: formatDate(startDate),
      end: formatDate(endDate),
    };
  }, []);

  React.useEffect(() => {
    if (selectedRange === "7days") {
      const { start, end } = getLast7Days();
      onDateRangeChange(start, end);
    } else if (selectedRange === "30days") {
      const { start, end } = getLast30Days();
      onDateRangeChange(start, end);
    }
  }, [selectedRange, onDateRangeChange, getLast30Days, getLast7Days]);

  const handlePresetClick = (range: "7days" | "30days") => {
    setSelectedRange(range);
  };

  const handleCustomDateChange = useCallback(() => {
    if (customStartDate && customEndDate) {
      onDateRangeChange(customStartDate, customEndDate);
      setSelectedRange("custom");
    }
  }, [customStartDate, customEndDate, onDateRangeChange]);

  const handleCustomRangeClick = () => {
    setSelectedRange("custom");
    if (!customStartDate || !customEndDate) {
      const { start, end } = getLast7Days();
      setCustomStartDate(start);
      setCustomEndDate(end);
      onDateRangeChange(start, end);
    }
  };

  React.useEffect(() => {
    if (selectedRange === "custom") {
      handleCustomDateChange();
    }
  }, [customStartDate, customEndDate, handleCustomDateChange, selectedRange]);

  return (
    <DateRangeContainer onClick={(e) => e.stopPropagation()}>
      <PresetButton
        isActive={selectedRange === "7days"}
        onClick={(e) => {
          e.stopPropagation();
          handlePresetClick("7days");
        }}
      >
        7 derniers jours
      </PresetButton>

      <PresetButton
        isActive={selectedRange === "30days"}
        onClick={(e) => {
          e.stopPropagation();
          handlePresetClick("30days");
        }}
      >
        30 derniers jours
      </PresetButton>

      <PresetButton
        isActive={selectedRange === "custom"}
        onClick={(e) => {
          e.stopPropagation();
          handleCustomRangeClick();
        }}
      >
        Période personnalisée
      </PresetButton>

      {selectedRange === "custom" && (
        <CustomDateContainer onClick={(e) => e.stopPropagation()}>
          <DateInput
            type="date"
            value={customStartDate}
            onChange={(e) => {
              e.stopPropagation();
              setCustomStartDate(e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <DateSeparator>à</DateSeparator>
          <DateInput
            type="date"
            value={customEndDate}
            onChange={(e) => {
              e.stopPropagation();
              setCustomEndDate(e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </CustomDateContainer>
      )}
    </DateRangeContainer>
  );
};

export default DateRangePicker;
